// Library methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// Components
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { WarningIcon } from "src/assets/icons";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Hooks
import { useEMRProblemsAcrossHubs } from "src/services/emr/hooks/emr-problems/useEMRHubProblems";

// Types
import { EMRHubType } from "src/services/emr/types";

type HubProblemBannerType = {
  emrHubs: Array<EMRHubType>;
};

const HubProblemBanner = ({ emrHubs }: HubProblemBannerType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const hubIds = useMemo(
    () =>
      emrHubs
        .map(({ hubId }) => hubId)
        .filter((hubId): hubId is string => Boolean(hubId)),
    [emrHubs]
  );

  const hubProblems = useEMRProblemsAcrossHubs(clinicInfo?.id, hubIds);

  const affectedHubs = useMemo(
    () =>
      emrHubs.filter(({ hubId }) =>
        hubProblems.some((problem) => problem.hubId === hubId)
      ),
    [emrHubs, hubProblems]
  );

  if (hubProblems.length === 0) return null;

  const onlyHub = affectedHubs.length === 1 ? affectedHubs[0] : undefined;

  return (
    <Grid
      item
      data-testid="hub-problem-banner"
      bgcolor="#E2772E"
      width="100%"
      padding="8px"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <WarningIcon width="16px" height="16px" style={{ color: "#FFF" }} />
        <Typography fontSize="14px" color="#FFF">
          {hubProblems.length}{" "}
          {onlyHub
            ? t("EMR Problem Reported on {{hub}}.", {
                count: hubProblems.length,
                hub: onlyHub.alias,
              })
            : t("EMR Problem Reported.", { count: hubProblems.length })}
        </Typography>
      </Box>

      <Link
        to={onlyHub ? `/data-hub/${onlyHub.hubId}/connections` : "/data-hub"}
        style={{
          fontSize: "14px",
          fontWeight: 600,
          color: "#FFF",
          textDecoration: "underline",
          whiteSpace: "nowrap",
          flexShrink: 0,
          marginLeft: "6px",
        }}
      >
        {t("View Hub")}
      </Link>
    </Grid>
  );
};

export default HubProblemBanner;
